/**
 * Balance Day Experience
 * Full day experience with Heart Reset → Immersion seamless flow.
 * Heart Reset plays inline with actual audio then automatically transitions to Guided Immersion.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Heart, 
  Play,
  Check,
  ChevronRight,
  Moon,
  Sun,
  Sparkles,
  Pencil,
  Pause,
  Volume2,
  VolumeX,
  Anchor,
  Eye,
  Wind,
  Smile,
  RotateCcw
} from "lucide-react";
import axios from "axios";
import { voiceVolume, getStoredAudioMode } from "../../utils/modeAudio";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session types for the day
const SESSION_TYPES = {
  HEART_RESET: "heart_reset",
  IMMERSION: "immersion",
  WRITING: "writing",
  DAILY_RESET: "daily_reset",
  NIGHT_AUDIO: "night_audio"
};

// Writing exercise prompts per day
const WRITING_PROMPTS = {
  1: {
    title: "What are you noticing?",
    prompt: "Write about a moment of comparison you noticed today. What were you believing in that moment? Don't filter — just let it flow.",
    duration: 180 // 3 minutes
  },
  2: {
    title: "What patterns emerged?",
    prompt: "Write about any recurring thoughts or stories that showed up today. What do you see when you step back?",
    duration: 180
  },
  3: {
    title: "What do you want to release?",
    prompt: "Write everything you're carrying right now. The tension. The weight. The stories. Pour it all out onto the page. Then... let it go.",
    duration: 180
  },
  4: {
    title: "What is true about you?",
    prompt: "Write about who you really are — not who you think you should be. What do you know to be true when you return to yourself?",
    duration: 180
  },
  5: {
    title: "What will you choose?",
    prompt: "Write about the state you want to carry forward. How will you move through life differently after this journey?",
    duration: 180
  }
};

// Daily Reset configs - maps each day to its specific reset with synced text phases
const DAILY_RESET_CONFIGS = {
  1: {
    name: "Ground Reset",
    voiceAudio: '/api/audio/voice/reset_breathe.m4a',
    audioDuration: 95,
    icon: "anchor",
    color: "from-emerald-500 to-teal-500",
    bgGradient: "from-emerald-950/95 via-teal-900/90 to-slate-950",
    glowColor: "rgba(16,185,129,0.3)",
    instructions: [
      "Feel your feet on the ground...",
      "Feel the weight of your body...",
      "Let your awareness come back to the present moment..."
    ],
    phases: [
      { start: 0, text: "" },
      { start: 1, text: "Pause..." },
      { start: 5, text: "Close your eyes..." },
      { start: 10, text: "Take a deep breath..." },
      { start: 16, text: "Feel your body begin to settle..." },
      { start: 24, text: "Another breath..." },
      { start: 32, text: "Feel your feet on the ground..." },
      { start: 40, text: "Feel the weight of your body..." },
      { start: 50, text: "You are here..." },
      { start: 60, text: "Present..." },
      { start: 70, text: "Grounded..." },
      { start: 80, text: "Return to the present moment..." },
      { start: 90, text: "" }
    ]
  },
  2: {
    name: "Stillness Reset",
    voiceAudio: '/api/audio/voice/reset_still.m4a',
    audioDuration: 108, // Actual audio is ~107.76s
    icon: "eye",
    color: "from-indigo-500 to-violet-500",
    bgGradient: "from-indigo-950/95 via-violet-900/90 to-slate-950",
    glowColor: "rgba(99,102,241,0.3)",
    instructions: [
      "Breathe into your belly...",
      "I wonder what my next thought will be...",
      "And listen..."
    ],
    phases: [
      { start: 0, text: "" },
      { start: 1, text: "Pause..." },
      { start: 5, text: "Let your eyes close..." },
      { start: 10, text: "Breathe into your belly..." },
      { start: 18, text: "Let everything settle..." },
      { start: 28, text: "Now gently ask yourself..." },
      { start: 35, text: "I wonder what my next thought will be..." },
      { start: 50, text: "And listen..." },
      { start: 65, text: "You are the awareness..." },
      { start: 78, text: "Not the thought..." },
      { start: 88, text: "Stay here..." },
      { start: 100, text: "In stillness..." },
      { start: 108, text: "" }
    ]
  },
  3: {
    name: "Let Go Reset",
    voiceAudio: '/api/audio/voice/reset_letgo.m4a',
    audioDuration: 92,
    icon: "wind",
    color: "from-cyan-500 to-blue-500",
    bgGradient: "from-cyan-950/95 via-blue-900/90 to-slate-950",
    glowColor: "rgba(6,182,212,0.3)",
    instructions: [
      "Take a deep breath in...",
      "Lift your shoulders slightly... hold...",
      "Exhale slowly... let everything drop...",
      "Feel the release..."
    ],
    phases: [
      { start: 0, text: "" },
      { start: 1, text: "Pause..." },
      { start: 5, text: "Close your eyes..." },
      { start: 10, text: "Take a deep breath in..." },
      { start: 18, text: "Lift your shoulders slightly..." },
      { start: 25, text: "Hold..." },
      { start: 30, text: "And exhale... let everything drop..." },
      { start: 40, text: "Feel the release..." },
      { start: 50, text: "Again... breathe in..." },
      { start: 58, text: "Lift... hold..." },
      { start: 65, text: "And let go..." },
      { start: 75, text: "Nothing to carry..." },
      { start: 85, text: "Just release..." },
      { start: 90, text: "" }
    ]
  },
  4: {
    name: "Heart Reset",
    voiceAudio: '/api/audio/voice/reset_heart.m4a',
    audioDuration: 102,
    icon: "heart",
    color: "from-rose-500 to-pink-500",
    bgGradient: "from-rose-950/95 via-pink-900/90 to-slate-950",
    glowColor: "rgba(251,113,133,0.3)",
    instructions: [
      "Breathe into your belly...",
      "Then into your heart...",
      "Feel gratitude expand..."
    ],
    phases: [
      { start: 0, text: "" },
      { start: 0.2, text: "Pause..." },
      { start: 3.9, text: "Gently close your eyes..." },
      { start: 9.0, text: "Slowly breathe..." },
      { start: 14.1, text: "Deep... soft... natural..." },
      { start: 20.0, text: "Allow that breath to rise into your heart..." },
      { start: 31.2, text: "Feel your chest gently expand..." },
      { start: 36.1, text: "Feel the warmth..." },
      { start: 40.2, text: "The openness..." },
      { start: 45.2, text: "Now tune into something you are grateful for..." },
      { start: 56.1, text: "Someone you love... a moment... a blessing..." },
      { start: 65.4, text: "Breathe that feeling into your heart..." },
      { start: 72.3, text: "Let it gently spread through your body..." },
      { start: 80.8, text: "Rest here..." },
      { start: 90.9, text: "Carry this love with you..." },
      { start: 100, text: "" }
    ]
  },
  5: {
    name: "Smile Reset",
    voiceAudio: '/api/audio/voice/reset_smile.m4a',
    audioDuration: 78,
    icon: "smile",
    color: "from-amber-500 to-orange-500",
    bgGradient: "from-amber-950/95 via-orange-900/90 to-slate-950",
    glowColor: "rgba(245,158,11,0.3)",
    instructions: [
      "Bring awareness into your body...",
      "Allow a soft smile to form...",
      "Feel it spread...",
      "Let your state lift naturally..."
    ],
    phases: [
      { start: 0, text: "" },
      { start: 1, text: "Pause..." },
      { start: 5, text: "Close your eyes..." },
      { start: 10, text: "Bring awareness into your body..." },
      { start: 18, text: "Notice how you feel..." },
      { start: 26, text: "Now... allow a soft smile to form..." },
      { start: 35, text: "Just the beginning of a smile..." },
      { start: 42, text: "Feel it spread..." },
      { start: 50, text: "Let it reach your eyes..." },
      { start: 58, text: "Feel your state lift..." },
      { start: 66, text: "Naturally... gently..." },
      { start: 74, text: "" }
    ]
  }
};

// Actual Heart Reset phases - synced to the recorded audio (text appears slightly ahead)
const HEART_RESET_CONFIG = {
  voiceAudio: '/api/audio/voice/reset_heart.m4a',
  audioDuration: 102,
  closingMessage: 'Return to your heart... and carry this love with you into your day.',
  phases: [
    { start: 0, text: '', animation: 'neutral' },
    { start: 0.2, text: 'Pause...', animation: 'neutral' },
    { start: 3.9, text: 'Gently close your eyes...', animation: 'soften' },
    { start: 9.0, text: 'Slowly breathe...', animation: 'belly-breathe' },
    { start: 14.1, text: 'Deep...', animation: 'belly-breathe' },
    { start: 14.8, text: 'soft...', animation: 'belly-breathe' },
    { start: 16.0, text: 'natural...', animation: 'belly-breathe' },
    { start: 20.0, text: 'Allow that breath... that energy...', animation: 'rise-to-heart' },
    { start: 25.1, text: 'To rise up into your heart space...', animation: 'rise-to-heart' },
    { start: 31.2, text: 'Feel your chest gently expand...', animation: 'heart-expand' },
    { start: 36.1, text: 'Feel the warmth...', animation: 'heart-glow' },
    { start: 40.2, text: 'The openness...', animation: 'heart-glow' },
    { start: 45.2, text: 'Now tune into something that makes you truly grateful...', animation: 'heart-glow' },
    { start: 52.1, text: 'Someone you love...', animation: 'heart-radiate' },
    { start: 56.1, text: 'A moment...', animation: 'heart-radiate' },
    { start: 59.4, text: 'A simple blessing in your life...', animation: 'heart-radiate' },
    { start: 65.4, text: 'Breathe that feeling into your heart...', animation: 'heart-fill' },
    { start: 72.3, text: 'And let it gently spread through your body...', animation: 'heart-spread' },
    { start: 80.8, text: 'Rest here...', animation: 'heart-rest' },
    { start: 90.9, text: 'Return to your heart... and carry this love with you...', animation: 'neutral' },
    { start: 98, text: '', animation: 'closing' }
  ]
};

const BalanceDayPage = () => {
  const navigate = useNavigate();
  const { journeyId, dayNumber } = useParams();
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionPhase, setSessionPhase] = useState(0);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [immersionLayer, setImmersionLayer] = useState(0);
  const [nightAudioPlaying, setNightAudioPlaying] = useState(false);
  const [currentAffirmation, setCurrentAffirmation] = useState(0);
  const [heartResetComplete, setHeartResetComplete] = useState(false);
  const [transitioningToImmersion, setTransitioningToImmersion] = useState(false);
  
  // Heart Reset audio state
  const [heartElapsedTime, setHeartElapsedTime] = useState(0);
  const [heartIsPlaying, setHeartIsPlaying] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const heartAudioRef = useRef(null);
  const heartTimerRef = useRef(null);
  
  // Daily Reset state
  const [dailyResetPhase, setDailyResetPhase] = useState('intro'); // intro, playing, complete
  const [dailyResetElapsed, setDailyResetElapsed] = useState(0);
  const [dailyResetPlaying, setDailyResetPlaying] = useState(false);
  const dailyResetAudioRef = useRef(null);
  const dailyResetTimerRef = useRef(null);
  
  // Writing exercise state
  const [writingText, setWritingText] = useState('');
  const [writingTimeLeft, setWritingTimeLeft] = useState(180);
  const [writingStarted, setWritingStarted] = useState(false);
  const writingTimerRef = useRef(null);
  
  // Evening reflection state
  const [eveningReflection, setEveningReflection] = useState('');
  const [showReflectionInput, setShowReflectionInput] = useState(false);
  
  const getToken = () => localStorage.getItem("reclaim_token");
  const day = parseInt(dayNumber);

  // Define handleLogSession early as a useCallback so it can be used in effects
  const handleLogSession = useCallback(async (sessionType) => {
    try {
      await axios.post(
        `${API_URL}/api/balance/journey/${journeyId}/session`,
        { day_number: day, session_type: sessionType },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (err) {
      console.error("Error logging session:", err);
    }
  }, [journeyId, day]);

  // Load day data
  const loadDayData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/balance/journey/${journeyId}/day/${day}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setDayData(response.data);
      setActionCompleted(response.data.action_completed || false);
    } catch (err) {
      console.error("Error loading day:", err);
    } finally {
      setLoading(false);
    }
  }, [journeyId, day]);

  useEffect(() => {
    loadDayData();
  }, [loadDayData]);

  // Heart Reset audio timer - syncs with actual audio playback
  useEffect(() => {
    if (activeSession !== SESSION_TYPES.HEART_RESET || !heartIsPlaying) {
      if (heartTimerRef.current) clearInterval(heartTimerRef.current);
      return;
    }
    
    heartTimerRef.current = setInterval(() => {
      if (heartAudioRef.current && heartAudioRef.current.currentTime > 0) {
        const time = heartAudioRef.current.currentTime;
        setHeartElapsedTime(time);
        
        // Check if complete
        if (time >= HEART_RESET_CONFIG.audioDuration - 0.5) {
          setHeartIsPlaying(false);
          setHeartResetComplete(true);
          clearInterval(heartTimerRef.current);
          
          // Transition to immersion
          setTransitioningToImmersion(true);
          handleLogSession("heart_reset");
          setTimeout(() => {
            setActiveSession(SESSION_TYPES.IMMERSION);
            setTransitioningToImmersion(false);
            setHeartElapsedTime(0);
          }, 2000);
        }
      }
    }, 50);
    
    return () => {
      if (heartTimerRef.current) clearInterval(heartTimerRef.current);
    };
  }, [activeSession, heartIsPlaying]);

  // Immersion auto-advance effect - silent, single phrase at a time
  // Timing: fade in (1.5s) + stay (3s) + fade out (1.5s) = 6s per phrase
  useEffect(() => {
    if (activeSession !== SESSION_TYPES.IMMERSION || !dayData) return;
    
    const phrases = dayData.immersion?.phrases || [];
    if (phrases.length === 0) return;
    
    // Total time per phrase: 6 seconds (fade in + stay + fade out)
    const timePerPhrase = 6;
    
    const timer = setTimeout(() => {
      if (sessionPhase < phrases.length - 1) {
        // Move to next phrase
        setSessionPhase(prev => prev + 1);
      } else {
        // Immersion complete - transition to writing exercise
        handleLogSession("immersion");
        setSessionPhase(0);
        // Small pause before writing
        setTimeout(() => {
          setActiveSession(SESSION_TYPES.WRITING);
          setWritingText('');
          setWritingTimeLeft(WRITING_PROMPTS[day]?.duration || 180);
          setWritingStarted(false);
        }, 1500);
      }
    }, timePerPhrase * 1000);
    
    return () => clearTimeout(timer);
  }, [activeSession, sessionPhase, dayData, handleLogSession, day]);

  // Writing timer effect - must be at top level
  useEffect(() => {
    if (activeSession !== SESSION_TYPES.WRITING || !writingStarted || writingTimeLeft <= 0) {
      return;
    }
    
    writingTimerRef.current = setInterval(() => {
      setWritingTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(writingTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (writingTimerRef.current) clearInterval(writingTimerRef.current);
    };
  }, [activeSession, writingStarted, writingTimeLeft]);

  const handleCompleteAction = async () => {
    try {
      await axios.post(
        `${API_URL}/api/balance/journey/${journeyId}/day/${day}/complete-action`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setActionCompleted(true);
      setShowReflectionInput(false);
    } catch (err) {
      console.error("Error completing action:", err);
    }
  };

  // Save evening reflection
  const handleSaveReflection = async () => {
    if (!eveningReflection.trim()) return;
    
    try {
      await axios.post(
        `${API_URL}/api/balance/journey/${journeyId}/day/${day}/reflection`,
        { reflection: eveningReflection },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setActionCompleted(true);
      setShowReflectionInput(false);
    } catch (err) {
      console.error("Error saving reflection:", err);
      // Still mark as complete even if save fails
      setActionCompleted(true);
      setShowReflectionInput(false);
    }
  };

  const handleCompleteDay = async () => {
    try {
      await axios.post(
        `${API_URL}/api/balance/journey/${journeyId}/day/${day}/complete`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      navigate(`/balance/${journeyId}`);
    } catch (err) {
      console.error("Error completing day:", err);
    }
  };

  const startHeartResetFlow = () => {
    // Start inline Heart Reset with actual audio
    setHeartElapsedTime(0);
    setHeartResetComplete(false);
    setActiveSession(SESSION_TYPES.HEART_RESET);
    
    // Create and play the audio
    const audio = new Audio(`${API_URL}${HEART_RESET_CONFIG.voiceAudio}`);
    audio.volume = voiceVolume(getStoredAudioMode());
    heartAudioRef.current = audio;
    
    audio.onplay = () => {
      setHeartIsPlaying(true);
    };
    
    audio.onended = () => {
      setHeartIsPlaying(false);
      setHeartResetComplete(true);
      // Transition will be handled by the timer effect
    };
    
    audio.play().catch(err => {
      console.log('[Heart Reset] Autoplay blocked:', err);
      // Show play button for manual start
      setHeartIsPlaying(false);
    });
  };

  // Get current phase based on elapsed time
  const getCurrentHeartPhase = () => {
    const phases = HEART_RESET_CONFIG.phases;
    for (let i = phases.length - 1; i >= 0; i--) {
      if (heartElapsedTime >= phases[i].start) {
        return phases[i];
      }
    }
    return phases[0];
  };

  // Manual play for iOS
  const handleHeartManualPlay = () => {
    if (heartAudioRef.current && !heartIsPlaying) {
      heartAudioRef.current.play().then(() => {
        setHeartIsPlaying(true);
      }).catch(err => {
        console.log('[Heart Reset] Manual play failed:', err);
      });
    }
  };

  // Close Heart Reset
  const closeHeartReset = () => {
    if (heartAudioRef.current) {
      heartAudioRef.current.pause();
      heartAudioRef.current = null;
    }
    if (heartTimerRef.current) clearInterval(heartTimerRef.current);
    setHeartIsPlaying(false);
    setHeartElapsedTime(0);
    setActiveSession(null);
  };

  // ========== DAILY RESET FUNCTIONS ==========
  const dailyResetConfig = DAILY_RESET_CONFIGS[day];
  
  const startDailyReset = () => {
    setDailyResetPhase('intro');
    setDailyResetElapsed(0);
    setDailyResetPlaying(false);
    setActiveSession(SESSION_TYPES.DAILY_RESET);
  };

  const playDailyResetAudio = () => {
    if (!dailyResetConfig) return;
    
    const audio = new Audio(`${API_URL}${dailyResetConfig.voiceAudio}`);
    audio.volume = voiceVolume(getStoredAudioMode());
    dailyResetAudioRef.current = audio;
    
    audio.onplay = () => {
      setDailyResetPlaying(true);
      setDailyResetPhase('playing');
      
      // Start timer
      dailyResetTimerRef.current = setInterval(() => {
        if (dailyResetAudioRef.current) {
          setDailyResetElapsed(dailyResetAudioRef.current.currentTime);
        }
      }, 100);
    };
    
    audio.onended = () => {
      setDailyResetPlaying(false);
      setDailyResetPhase('complete');
      if (dailyResetTimerRef.current) clearInterval(dailyResetTimerRef.current);
      handleLogSession("daily_reset");
    };
    
    audio.play().catch(err => {
      console.log('[Daily Reset] Autoplay blocked:', err);
    });
  };

  const closeDailyReset = () => {
    if (dailyResetAudioRef.current) {
      dailyResetAudioRef.current.pause();
      dailyResetAudioRef.current = null;
    }
    if (dailyResetTimerRef.current) clearInterval(dailyResetTimerRef.current);
    setDailyResetPlaying(false);
    setDailyResetElapsed(0);
    setDailyResetPhase('intro');
    setActiveSession(null);
  };

  // Get current phase text based on elapsed time for daily reset
  const getCurrentDailyResetPhase = () => {
    if (!dailyResetConfig?.phases) return { text: "" };
    const phases = dailyResetConfig.phases;
    for (let i = phases.length - 1; i >= 0; i--) {
      if (dailyResetElapsed >= phases[i].start) {
        return phases[i];
      }
    }
    return phases[0] || { text: "" };
  };

  // Get icon component for daily reset
  const getDailyResetIcon = () => {
    const iconMap = {
      anchor: Anchor,
      eye: Eye,
      wind: Wind,
      heart: Heart,
      smile: Smile
    };
    const IconComponent = iconMap[dailyResetConfig?.icon] || Heart;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Heart size={40} className="text-violet-400" />
        </motion.div>
      </div>
    );
  }

  if (!dayData) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
        <p className="text-white/60">Day not found</p>
      </div>
    );
  }

  // ========== HEART RESET EXPERIENCE (INLINE WITH AUDIO) ==========
  if (activeSession === SESSION_TYPES.HEART_RESET) {
    const currentPhase = getCurrentHeartPhase();
    const progress = (heartElapsedTime / HEART_RESET_CONFIG.audioDuration) * 100;

    // Transition screen
    if (transitioningToImmersion) {
      return (
        <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-8 text-center">
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0.8, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500/30 to-violet-500/30 flex items-center justify-center mb-8"
          >
            <Heart size={36} className="text-pink-400" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-white/60 text-lg"
          >
            Heart centered...
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/40 text-sm mt-2"
          >
            Moving into your immersion...
          </motion.p>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-950/95 via-pink-900/90 to-slate-950 flex flex-col relative overflow-hidden">
        {/* Ambient heart glow */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              "radial-gradient(circle at 50% 40%, rgba(251,113,133,0.2), transparent 60%)",
              "radial-gradient(circle at 50% 40%, rgba(251,113,133,0.35), transparent 60%)",
              "radial-gradient(circle at 50% 40%, rgba(251,113,133,0.2), transparent 60%)"
            ]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />

        {/* Header */}
        <div className="p-6 flex items-center justify-between relative z-10">
          <button
            onClick={closeHeartReset}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          
          <span className="text-white/40 text-xs uppercase tracking-widest">Heart Reset</span>
          
          <button
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm"
          >
            {voiceEnabled ? (
              <Volume2 size={20} className="text-white" />
            ) : (
              <VolumeX size={20} className="text-white/50" />
            )}
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center relative z-10">
          {/* Breathing heart animation */}
          <motion.div
            animate={{
              scale: heartIsPlaying ? [1, 1.15, 1] : 1,
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="w-32 h-32 rounded-full flex items-center justify-center mb-12"
            style={{
              background: 'radial-gradient(circle, rgba(251,113,133,0.3) 0%, transparent 70%)',
              boxShadow: heartIsPlaying ? '0 0 60px 30px rgba(253,164,175,0.3)' : 'none'
            }}
          >
            <motion.div
              animate={{
                scale: heartIsPlaying ? [1, 1.1, 1] : 1,
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
            >
              <Heart size={56} className="text-rose-400" fill="rgba(251,113,133,0.3)" />
            </motion.div>
          </motion.div>

          {/* Current phase text */}
          <AnimatePresence mode="wait">
            <motion.p
              key={currentPhase.text}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="text-white/60 text-2xl font-light leading-relaxed max-w-sm"
            >
              {currentPhase.text || (heartResetComplete ? HEART_RESET_CONFIG.closingMessage : '')}
            </motion.p>
          </AnimatePresence>

          {/* Play button if not started */}
          {!heartIsPlaying && !heartResetComplete && heartElapsedTime < 1 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleHeartManualPlay}
              className="mt-12 w-20 h-20 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center shadow-2xl"
              style={{ boxShadow: '0 0 40px 15px rgba(251,113,133,0.35)' }}
            >
              <Play size={32} className="text-white ml-1" />
            </motion.button>
          )}
        </div>

        {/* Progress bar */}
        <div className="p-6 relative z-10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/30 text-xs">Heart Reset</span>
            <span className="text-white/30 text-xs">
              {Math.floor(heartElapsedTime)}s / {HEART_RESET_CONFIG.audioDuration}s
            </span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-rose-400 to-pink-400"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <p className="text-white/20 text-xs text-center mt-4">
            Then seamlessly into your Guided Immersion
          </p>
        </div>
      </div>
    );
  }

  // ========== SILENT IMMERSION (STATE ABSORPTION) ==========
  if (activeSession === SESSION_TYPES.IMMERSION) {
    const phrases = dayData.immersion?.phrases || [];
    const theme = dayData.immersion?.theme || "";
    const currentPhrase = phrases[sessionPhase] || "";
    const progress = ((sessionPhase + 1) / phrases.length) * 100;

    return (
      <div 
        className="min-h-screen flex flex-col relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0a0a0f 0%, #0d0d14 50%, #0a0a0f 100%)'
        }}
        onClick={() => {
          // Tap to exit (optional)
          handleLogSession("immersion");
          setActiveSession(null);
          setSessionPhase(0);
        }}
      >
        {/* Subtle animated gradient overlay */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              'radial-gradient(ellipse at 30% 20%, rgba(139,92,246,0.03) 0%, transparent 50%)',
              'radial-gradient(ellipse at 70% 80%, rgba(236,72,153,0.03) 0%, transparent 50%)',
              'radial-gradient(ellipse at 50% 50%, rgba(139,92,246,0.03) 0%, transparent 50%)',
              'radial-gradient(ellipse at 30% 20%, rgba(139,92,246,0.03) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />

        {/* Very subtle noise texture overlay */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.02]"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%" height="100%" filter="url(%23noise)"/%3E%3C/svg%3E")'
          }}
        />

        {/* Slow moving light haze */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            opacity: [0.02, 0.04, 0.02]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{
            background: 'radial-gradient(circle at 50% 40%, rgba(255,255,255,0.08) 0%, transparent 60%)'
          }}
        />

        {/* Main content - centered phrase */}
        <div className="flex-1 flex flex-col items-center justify-center px-10 text-center relative z-10">
          <AnimatePresence mode="wait">
            <motion.p
              key={sessionPhase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ 
                duration: 1.5,
                ease: "easeInOut"
              }}
              className="text-2xl md:text-3xl leading-relaxed max-w-md"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                color: 'rgba(255, 253, 250, 0.7)',
                textShadow: '0 0 40px rgba(255,255,255,0.05)',
                fontWeight: 400,
                letterSpacing: '0.02em'
              }}
            >
              {currentPhrase}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Minimal progress - just subtle dots */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
          {phrases.map((_, idx) => (
            <motion.div
              key={idx}
              className="w-1 h-1 rounded-full"
              animate={{
                backgroundColor: idx <= sessionPhase 
                  ? 'rgba(255,255,255,0.3)' 
                  : 'rgba(255,255,255,0.08)'
              }}
              transition={{ duration: 0.5 }}
            />
          ))}
        </div>

        {/* Theme label - extremely subtle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.15 }}
          className="absolute top-8 left-0 right-0 text-center text-[10px] uppercase tracking-[0.3em] text-white pointer-events-none"
        >
          {theme}
        </motion.p>
      </div>
    );
  }

  // ========== WRITING EXERCISE ==========
  if (activeSession === SESSION_TYPES.WRITING) {
    const writingPrompt = WRITING_PROMPTS[day];

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const completeWriting = () => {
      if (writingTimerRef.current) clearInterval(writingTimerRef.current);
      handleLogSession("writing");
      setActiveSession(null);
      setWritingText('');
      setWritingStarted(false);
    };

    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
        {/* Header */}
        <div className="p-6 flex items-center justify-between">
          <button
            onClick={completeWriting}
            className="text-white/40 hover:text-white/60 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <span className="text-white/30 text-xs uppercase tracking-widest">
            Day {day} • Writing
          </span>
          <div className="w-6" />
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-6">
          {/* Prompt */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="text-xl font-light text-white mb-3">
              {writingPrompt?.title}
            </h2>
            <p className="text-white/50 text-sm leading-relaxed">
              {writingPrompt?.prompt}
            </p>
          </motion.div>

          {/* Writing area */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 relative"
          >
            <textarea
              value={writingText}
              onChange={(e) => {
                setWritingText(e.target.value);
                if (!writingStarted && e.target.value.length > 0) {
                  setWritingStarted(true);
                }
              }}
              placeholder="Start writing... let it flow without filtering..."
              className="w-full h-full min-h-[300px] bg-white/5 rounded-2xl p-5 text-white/80 placeholder-white/20 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/30 text-base leading-relaxed"
              style={{ fontFamily: 'Georgia, serif' }}
              data-testid="writing-textarea"
            />
            
            {/* Timer */}
            {writingStarted && (
              <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <span className={`text-sm font-mono ${writingTimeLeft <= 30 ? 'text-amber-400' : 'text-white/40'}`}>
                  {formatTime(writingTimeLeft)}
                </span>
              </div>
            )}
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 space-y-3"
          >
            {writingTimeLeft === 0 || writingText.length > 50 ? (
              <button
                onClick={completeWriting}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 text-white font-medium"
                data-testid="complete-writing-btn"
              >
                Complete & Release
              </button>
            ) : (
              <p className="text-white/20 text-xs text-center">
                {writingStarted 
                  ? "Keep writing... let it all out" 
                  : "Start typing to begin the timer"}
              </p>
            )}
            
            <button
              onClick={completeWriting}
              className="w-full py-3 rounded-xl text-white/30 text-sm hover:text-white/50 transition-colors"
            >
              Skip for now
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ========== DAILY RESET SESSION ==========
  if (activeSession === SESSION_TYPES.DAILY_RESET && dailyResetConfig) {
    const DailyIcon = getDailyResetIcon();
    const progress = (dailyResetElapsed / dailyResetConfig.audioDuration) * 100;

    return (
      <div className={`min-h-screen bg-gradient-to-b ${dailyResetConfig.bgGradient} flex flex-col relative overflow-hidden`}>
        {/* Ambient glow */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              `radial-gradient(circle at 50% 40%, ${dailyResetConfig.glowColor}, transparent 60%)`,
              `radial-gradient(circle at 50% 40%, ${dailyResetConfig.glowColor.replace('0.3', '0.45')}, transparent 60%)`,
              `radial-gradient(circle at 50% 40%, ${dailyResetConfig.glowColor}, transparent 60%)`
            ]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />

        {/* Header */}
        <div className="p-6 flex items-center justify-between relative z-10">
          <button
            onClick={closeDailyReset}
            className="p-3 rounded-full bg-white/10 backdrop-blur-sm"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          
          <span className="text-white/60 text-sm font-medium">{dailyResetConfig.name}</span>
          
          <div className="w-11" /> {/* Spacer */}
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center relative z-10">
          
          {/* INTRO PHASE */}
          {dailyResetPhase === 'intro' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-sm"
            >
              {/* Icon */}
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
                className={`w-24 h-24 rounded-full bg-gradient-to-br ${dailyResetConfig.color} flex items-center justify-center mx-auto mb-10 shadow-2xl`}
                style={{ boxShadow: `0 0 60px 20px ${dailyResetConfig.glowColor}` }}
              >
                <DailyIcon size={44} className="text-white" />
              </motion.div>

              {/* Title */}
              <h2 className="text-2xl font-light text-white mb-4">
                {dailyResetConfig.name}
              </h2>
              <p className="text-white/40 text-sm mb-8">
                Day {day} • {dayData?.title}
              </p>

              {/* Instructions */}
              <div className="space-y-3 mb-10">
                {dailyResetConfig.instructions.map((instruction, idx) => (
                  <motion.p
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + idx * 0.15 }}
                    className="text-white/50 text-sm"
                  >
                    {instruction}
                  </motion.p>
                ))}
              </div>

              {/* Start button */}
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                onClick={playDailyResetAudio}
                className={`w-20 h-20 rounded-full bg-gradient-to-r ${dailyResetConfig.color} flex items-center justify-center shadow-2xl mx-auto`}
                style={{ boxShadow: `0 0 40px 15px ${dailyResetConfig.glowColor}` }}
              >
                <Play size={32} className="text-white ml-1" />
              </motion.button>

              <p className="text-white/20 text-xs mt-6">
                Return to this reset throughout your day
              </p>
            </motion.div>
          )}

          {/* PLAYING PHASE */}
          {dailyResetPhase === 'playing' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="w-full max-w-sm text-center"
            >
              {/* Breathing icon */}
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="w-28 h-28 rounded-full flex items-center justify-center mx-auto mb-10"
                style={{
                  background: `radial-gradient(circle, ${dailyResetConfig.glowColor} 0%, transparent 70%)`,
                  boxShadow: `0 0 60px 30px ${dailyResetConfig.glowColor}`
                }}
              >
                <DailyIcon size={48} className="text-white/80" />
              </motion.div>

              {/* Synced text */}
              <AnimatePresence mode="wait">
                <motion.p
                  key={getCurrentDailyResetPhase().text}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-white/60 text-xl font-light leading-relaxed min-h-[60px]"
                  style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                >
                  {getCurrentDailyResetPhase().text}
                </motion.p>
              </AnimatePresence>
            </motion.div>
          )}

          {/* COMPLETE PHASE */}
          {dailyResetPhase === 'complete' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-sm"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className={`w-20 h-20 rounded-full bg-gradient-to-br ${dailyResetConfig.color} flex items-center justify-center mx-auto mb-8`}
              >
                <Check size={36} className="text-white" />
              </motion.div>

              <h2 className="text-2xl font-light text-white mb-3">
                Reset Complete
              </h2>
              <p className="text-white/40 text-sm mb-10">
                Return here anytime you need this reset today
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setDailyResetPhase('intro');
                    setDailyResetElapsed(0);
                  }}
                  className={`w-full py-4 rounded-xl bg-gradient-to-r ${dailyResetConfig.color} text-white font-medium flex items-center justify-center gap-2`}
                >
                  <RotateCcw size={18} />
                  Do It Again
                </button>
                
                <button
                  onClick={closeDailyReset}
                  className="w-full py-4 rounded-xl bg-white/10 text-white/60 font-medium"
                >
                  Back to Day {day}
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Progress bar - only when playing */}
        {dailyResetPhase === 'playing' && (
          <div className="p-6 relative z-10">
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className={`h-full bg-gradient-to-r ${dailyResetConfig.color}`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <p className="text-white/30 text-xs text-center mt-3">
              {Math.floor(dailyResetElapsed)}s / {dailyResetConfig.audioDuration}s
            </p>
          </div>
        )}
      </div>
    );
  }

  // ========== NIGHT AUDIO SESSION ==========
  if (activeSession === SESSION_TYPES.NIGHT_AUDIO) {
    const affirmations = dayData.night_affirmations || [];

    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
        {/* Header */}
        <div className="p-6 flex items-center justify-between">
          <button
            onClick={() => {
              setActiveSession(null);
              setCurrentAffirmation(0);
              setNightAudioPlaying(false);
              handleLogSession("night_audio");
            }}
            className="text-white/40 text-sm"
          >
            Exit
          </button>
          <Moon size={20} className="text-violet-400" />
        </div>

        {/* Night affirmations */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <p className="text-violet-400/60 text-xs uppercase tracking-widest mb-8">
            Night Affirmations
          </p>

          <AnimatePresence mode="wait">
            <motion.p
              key={currentAffirmation}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="text-white text-xl font-light leading-relaxed max-w-sm"
            >
              {affirmations[currentAffirmation]}
            </motion.p>
          </AnimatePresence>

          {/* Auto-advance indicator */}
          <motion.div
            className="mt-12 w-32 h-1 bg-white/10 rounded-full overflow-hidden"
          >
            <motion.div
              className="h-full bg-violet-500"
              initial={{ width: "0%" }}
              animate={{ width: nightAudioPlaying ? "100%" : "0%" }}
              transition={{ duration: 6, ease: "linear" }}
              onAnimationComplete={() => {
                if (nightAudioPlaying && currentAffirmation < affirmations.length - 1) {
                  setCurrentAffirmation(prev => prev + 1);
                } else if (currentAffirmation >= affirmations.length - 1) {
                  handleLogSession("night_audio");
                  setActiveSession(null);
                }
              }}
            />
          </motion.div>
        </div>

        {/* Controls */}
        <div className="p-6 flex justify-center gap-4">
          <button
            onClick={() => setNightAudioPlaying(!nightAudioPlaying)}
            className="w-14 h-14 rounded-full bg-gradient-to-r from-violet-600 to-pink-600 flex items-center justify-center"
          >
            {nightAudioPlaying ? (
              <Pause size={24} className="text-white" />
            ) : (
              <Play size={24} className="text-white ml-1" />
            )}
          </button>
        </div>

        {/* Affirmation count */}
        <div className="pb-8 text-center">
          <p className="text-white/30 text-sm">
            {currentAffirmation + 1} of {affirmations.length}
          </p>
        </div>
      </div>
    );
  }

  // ========== MAIN DAY VIEW ==========
  const dailyPractice = dayData.daily_practice;
  const dailyAction = dayData.daily_action;
  
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <button
          onClick={() => navigate(`/balance/${journeyId}`)}
          className="text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <span className="text-white/40 text-sm">Day {day}</span>
      </div>

      {/* Day Title */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-6 pb-6 text-center"
      >
        <h1 className="text-2xl font-light text-white mb-2">
          {dayData.title}
        </h1>
        <p className="text-white/40 text-sm">{dayData.theme}</p>
        {dayData.reset_name && (
          <p className="text-violet-400/60 text-xs mt-1">+ {dayData.reset_name}</p>
        )}
      </motion.div>

      {/* Session Cards */}
      <div className="px-6 space-y-5 pb-8">
        {/* 1. Heart Reset + Immersion Combined Card */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          onClick={startHeartResetFlow}
          className="w-full p-5 rounded-2xl bg-gradient-to-br from-pink-500/10 via-violet-500/10 to-purple-500/10 border border-pink-500/20 text-left group hover:border-pink-500/40 transition-colors"
          data-testid="heart-reset-immersion-card"
        >
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-violet-500 flex items-center justify-center flex-shrink-0">
              <Heart size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium mb-0.5">Begin Today's Journey</h3>
              <p className="text-white/50 text-sm">Heart Reset → Guided Immersion</p>
            </div>
            <Play size={18} className="text-white/30 group-hover:text-white/60" />
          </div>
          
          {/* Progress indicators */}
          <div className="flex gap-2 ml-16">
            <div className="flex items-center gap-1.5 text-xs text-pink-400/60">
              <Heart size={10} />
              <span>Heart</span>
            </div>
            <ChevronRight size={12} className="text-white/20" />
            <div className="flex items-center gap-1.5 text-xs text-violet-400/60">
              <Sparkles size={10} />
              <span>Immersion</span>
            </div>
          </div>
        </motion.button>

        {/* 2. Daily Practice Card - Opens Daily Reset */}
        {dailyPractice && dailyResetConfig && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            onClick={startDailyReset}
            className="w-full p-6 rounded-2xl bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border border-violet-500/20 text-left group hover:border-violet-500/40 transition-colors"
            data-testid="daily-practice-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${dailyResetConfig.color} flex items-center justify-center`}>
                  {(() => {
                    const Icon = getDailyResetIcon();
                    return <Icon size={22} className="text-white" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-white font-medium">{dailyPractice.title}</h3>
                  <p className="text-white/40 text-xs">{dailyResetConfig.name}</p>
                </div>
              </div>
              <Play size={18} className="text-white/30 group-hover:text-white/60" />
            </div>
            
            {/* Practice Steps */}
            <div className="space-y-2 mb-4">
              {dailyPractice.steps?.slice(0, 3).map((step, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className="w-4 h-4 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-violet-400 text-[10px]">{idx + 1}</span>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">{step}</p>
                </div>
              ))}
              {dailyPractice.steps?.length > 3 && (
                <p className="text-violet-400/40 text-xs ml-6">+{dailyPractice.steps.length - 3} more steps</p>
              )}
            </div>
            
            {/* Cue */}
            <div className="pt-3 border-t border-violet-500/10">
              <p className="text-violet-400/60 text-xs italic">
                Tap to practice the {dailyResetConfig.name}
              </p>
            </div>
          </motion.button>
        )}

        {/* 2.5. Writing Exercise Card - Standalone access to writing */}
        {WRITING_PROMPTS[day] && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.18 }}
            onClick={() => {
              setActiveSession(SESSION_TYPES.WRITING);
              setWritingText('');
              setWritingTimeLeft(WRITING_PROMPTS[day]?.duration || 180);
              setWritingStarted(false);
            }}
            className="w-full p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 text-left group hover:border-amber-500/40 transition-colors"
            data-testid="writing-exercise-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Pencil size={22} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium mb-0.5">{WRITING_PROMPTS[day].title}</h3>
                <p className="text-white/40 text-xs">3-minute writing exercise</p>
              </div>
              <Play size={18} className="text-white/30 group-hover:text-white/60" />
            </div>
            
            <p className="text-amber-400/50 text-sm mt-3 ml-16 leading-relaxed line-clamp-2">
              {WRITING_PROMPTS[day].prompt}
            </p>
          </motion.button>
        )}

        {/* 3. Daily Action - End of Day Reflection */}
        {dailyAction && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className={`p-6 rounded-2xl border text-left ${
              actionCompleted 
                ? "bg-emerald-500/10 border-emerald-500/30" 
                : "bg-amber-500/5 border-amber-500/20"
            }`}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                actionCompleted ? "bg-emerald-500" : "bg-amber-500/20"
              }`}>
                {actionCompleted ? (
                  <Check size={18} className="text-white" />
                ) : (
                  <Pencil size={18} className="text-amber-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium mb-1">
                  {dailyAction.title || "End of Day"}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {dailyAction.description}
                </p>
              </div>
            </div>
            
            {/* Reflection Prompt */}
            <div className="pt-4 border-t border-white/5">
              <p className="text-white/30 text-xs mb-3">End-of-day reflection:</p>
              <p className="text-amber-400/80 text-sm italic mb-4">
                "{dailyAction.completion_prompt}"
              </p>
              
              {!actionCompleted && !showReflectionInput && (
                <button
                  onClick={() => setShowReflectionInput(true)}
                  className="w-full py-3 rounded-xl bg-amber-500/10 text-amber-400 text-sm font-medium hover:bg-amber-500/20 transition-colors"
                  data-testid="add-reflection-btn"
                >
                  Add Your Reflection
                </button>
              )}
              
              {/* Reflection Input */}
              {!actionCompleted && showReflectionInput && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-3"
                >
                  <textarea
                    value={eveningReflection}
                    onChange={(e) => setEveningReflection(e.target.value)}
                    placeholder="Write your reflection here..."
                    className="w-full h-32 bg-white/5 rounded-xl p-4 text-white/80 placeholder-white/20 resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/30 text-sm leading-relaxed"
                    style={{ fontFamily: 'Georgia, serif' }}
                    data-testid="evening-reflection-textarea"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveReflection}
                      disabled={!eveningReflection.trim()}
                      className="flex-1 py-3 rounded-xl bg-amber-500 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-600 transition-colors"
                      data-testid="save-reflection-btn"
                    >
                      Save & Complete Day
                    </button>
                    <button
                      onClick={() => {
                        setShowReflectionInput(false);
                        setEveningReflection('');
                      }}
                      className="px-4 py-3 rounded-xl text-white/40 text-sm hover:text-white/60 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}
              
              {actionCompleted && (
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <Check size={16} />
                  <span>Day complete</span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* 3. Interactive Element (if exists) */}
        {dayData.interactive_element && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => {
              if (dayData.interactive_element.type === "vision_board_link") {
                navigate(dayData.interactive_element.link_to);
              } else if (dayData.interactive_element.type === "future_letter_link") {
                navigate(dayData.interactive_element.link_to);
              } else if (dayData.interactive_element.type === "release_writing") {
                navigate(`/balance/${journeyId}/day/${day}/release`);
              }
            }}
            className="w-full p-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 text-left flex items-center gap-4 group hover:border-cyan-500/40 transition-colors"
            data-testid="interactive-element-card"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
              <Pencil size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium mb-0.5">
                {dayData.interactive_element.type === "release_writing" 
                  ? "Release Ritual" 
                  : dayData.interactive_element.type === "vision_board_link"
                    ? "Living Vision Board"
                    : "Future Letter"}
              </h3>
              <p className="text-white/50 text-sm">
                {dayData.interactive_element.prompt}
              </p>
            </div>
            <ChevronRight size={18} className="text-white/30 group-hover:text-white/60" />
          </motion.button>
        )}

        {/* 4. Night Audio */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          onClick={() => setActiveSession(SESSION_TYPES.NIGHT_AUDIO)}
          className="w-full p-5 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border border-indigo-500/20 text-left flex items-center gap-4 group hover:border-indigo-500/40 transition-colors"
          data-testid="night-audio-card"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center flex-shrink-0">
            <Moon size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-medium mb-0.5">Night Affirmations</h3>
            <p className="text-white/50 text-sm">{dayData.night_affirmations?.length} affirmations for sleep</p>
          </div>
          <Play size={18} className="text-white/30 group-hover:text-white/60" />
        </motion.button>
      </div>

      {/* Complete Day Button */}
      {actionCompleted && !dayData.is_completed && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-6 pb-8"
        >
          <button
            onClick={handleCompleteDay}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium"
            data-testid="complete-day-button"
          >
            Complete Day {day}
          </button>
        </motion.div>
      )}

      {/* Day complete indicator */}
      {dayData.is_completed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="px-6 pb-8 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-400">
            <Check size={16} />
            <span className="text-sm font-medium">Day Completed</span>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BalanceDayPage;
